import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePermissions } from '@/modules/common';
import { EntityIntegrationsRefreshCard } from '@/modules/common/components/organisms/entity-integrations-refresh-card';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  useBaseTab,
} from '@/modules/common/components/templates/feature-details';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { TeamMemberIntegrationsTable } from '@/modules/team/components/member-details/integrations/team-member-integrations-table';
import { useTeamMemberIntegrations } from '@/modules/team/hooks/use-team-member-integrations';
import { SettingsRoutes } from '@/router/routes';
import { compareFormValues } from '@/utils/form-state.utils';
import { logger } from '@/utils/logger/logger';

import {
  MemberStatus,
  TeamMember,
  TeamMemberFormData,
  TeamMemberIntegration,
  TeamMemberIntegrationsFormData,
} from '../../../types';

const TAG = 'TeamMemberIntegrationsTab:';

export interface TeamMemberIntegrationsTabProps
  extends BaseTabProps<TeamMember, TeamMemberIntegrationsFormData> {
  rawIntegrations?: IntegrationInfoResponse[];
}

export interface TeamMemberIntegrationsTabRef extends FeatureTabRef<TeamMemberFormData> {}

/**
 * Team member integrations tab component supporting create, edit, and read modes.
 * Clean and simple implementation with proper data merging.
 */
export const TeamMemberIntegrationsTab = forwardRef<
  TeamMemberIntegrationsTabRef,
  TeamMemberIntegrationsTabProps
>((props, ref) => {
  const { mode, featureData: teamMemberData, rawIntegrations, draftData } = props;

  const { t } = useTranslation(['authenticated.team']);
  const { t: tCommon } = useTranslation('common');

  const [integrations, setIntegrations] = useState<TeamMemberIntegration[]>([]);
  const [initialIntegrations, setInitialIntegrations] = useState<TeamMemberIntegration[]>([]);

  // Use the base tab hook directly since this tab doesn't use a traditional form
  const { isReadMode } = useBaseTab(props, {
    isArchivedFn: teamMember => teamMember?.status === MemberStatus.Archived,
    calculateDirtyStateFn: () => compareFormValues(integrations, initialIntegrations),
    calculateHasChangesFn: () => compareFormValues(integrations, initialIntegrations),
  });

  // Fetch available integrations
  const {
    data: availableIntegrationsData,
    isLoading,
    error,
  } = useTeamMemberIntegrations({
    mode,
    teamMemberId: teamMemberData?.id,
    rawIntegrations,
  });

  // Build final integrations data based on mode
  const modeBasedIntegrations = useMemo(() => {
    if (!availableIntegrationsData) return [];

    logger.debug(TAG, 'Building final integrations', { mode });

    if (mode === FeatureMode.Create) {
      // Create mode: merge with draft data if available
      if (draftData?.integrations) {
        return availableIntegrationsData.map(integration => {
          const draftIntegration = draftData.integrations?.find(di => di.id === integration.id);
          return {
            ...integration,
            selectedValue: draftIntegration?.selectedValue || null,
          };
        });
      }
      // No draft data, return as-is (no selections)
      return availableIntegrationsData;
    }

    // Edit/Read mode: data is already merged from query
    return availableIntegrationsData;
  }, [availableIntegrationsData, mode, draftData?.integrations]);

  // Update integrations state when final data changes
  useEffect(() => {
    if (modeBasedIntegrations.length > 0) {
      setIntegrations(modeBasedIntegrations);
      setInitialIntegrations(modeBasedIntegrations);
    }
  }, [modeBasedIntegrations]);

  // Handle team member selection change
  const handleTeamMemberChange = useCallback((integrationId: string, externalMemberId?: string) => {
    logger.debug(TAG, 'Team member selection changed', { integrationId, externalMemberId });

    setIntegrations(prev => {
      return prev.map(integration => {
        if (integration.id === integrationId) {
          let selectedValue: typeof integration.selectedValue = null;

          // Find the selected teamMember from the integration's team members
          if (externalMemberId && integration.teamMembers) {
            const selectedTeamMember = integration.teamMembers.find(p => p.id === externalMemberId);
            selectedValue = selectedTeamMember || null;
          }

          return {
            ...integration,
            selectedValue,
          };
        }
        return integration;
      });
    });
  }, []);

  /**
   * Custom form data getter for integrations
   */
  const getFormData = (): TeamMemberFormData => {
    return {
      integrations,
    } as TeamMemberFormData;
  };

  /**
   * Custom submit handler for integrations
   */
  const submitHandler = async (): Promise<void> => {
    logger.debug(TAG, 'Form submission requested');
    // No validation needed for integrations
  };

  const { hasPermission } = usePermissions();
  const canManageIntegrations = hasPermission(OpsiumPolicy.IntegrationsManage);

  const tabContent = (
    <div className="space-y-4">
      {/* Main integrations card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('details.tabs.integrations.title')}</CardTitle>
            {canManageIntegrations && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  window.open(SettingsRoutes.Integrations, '_blank');
                }}
                disabled={isReadMode}
              >
                {t('details.tabs.integrations.manageIntegrations')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <EntityIntegrationsRefreshCard
            entityType={EntityType.User}
            disabled={isReadMode}
            t={t}
            strings={{
              buttonTitle: t('details.tabs.integrations.refreshMembers'),
              buttonTitleRefreshing: t('details.tabs.integrations.refreshing'),
              descriptionI18nKey: 'details.tabs.integrations.refreshAlert',
              refreshButtonText: t('details.tabs.integrations.refreshButtonText'),
              successToast: t('details.tabs.integrations.refreshSuccess'),
              errorToast: t('details.tabs.integrations.refreshError'),
            }}
          />
          {/* Integrations table */}
          <TeamMemberIntegrationsTable
            integrations={integrations}
            isLoading={isLoading}
            error={error ? tCommon('error.generic') : null}
            isReadMode={isReadMode}
            onTeamMemberChange={handleTeamMemberChange}
          />
        </CardContent>
      </Card>
    </div>
  );

  return (
    <BaseFeatureTab
      {...props}
      ref={ref}
      isArchivedFn={teamMember => teamMember?.status === MemberStatus.Archived}
      onSubmit={submitHandler}
      isValid={true}
      getFormData={getFormData}
    >
      {tabContent}
    </BaseFeatureTab>
  );
});

TeamMemberIntegrationsTab.displayName = 'TeamMemberIntegrationsTab';
